import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@design-system';
import { RequestDetailDialog } from '@/features/illustration-monitor/components/request-detail-dialog';
import {
  RequestsFilterDraft,
  RequestsFilters,
} from '@/features/illustration-monitor/components/requests-filters';
import { RequestsTable } from '@/features/illustration-monitor/components/requests-table';
import { useMonitorRequests } from '@/features/illustration-monitor/hooks/use-illustration-monitor';
import { MonitorRequestRow, MonitorRequestsRequest } from '@/features/illustration-monitor/types';

const PAGE_SIZE = 25;

const EMPTY_DRAFT: RequestsFilterDraft = {
  search: '',
  status: '',
  createdFrom: '',
  createdTo: '',
};

function toRequest(draft: RequestsFilterDraft, page: number): MonitorRequestsRequest {
  return {
    search: draft.search.trim() || undefined,
    status: draft.status || undefined,
    createdFrom: draft.createdFrom || undefined,
    createdTo: draft.createdTo || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };
}

export function IllustrationMonitorRequestsPage() {
  const [draft, setDraft] = useState<RequestsFilterDraft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<RequestsFilterDraft>(EMPTY_DRAFT);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<MonitorRequestRow | null>(null);

  const requestsQuery = useMonitorRequests(toRequest(applied, page));

  const rows = requestsQuery.data?.requests ?? [];
  const total = requestsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleApply = () => {
    setApplied(draft);
    setPage(0);
  };

  const handleClear = () => {
    setDraft(EMPTY_DRAFT);
    setApplied(EMPTY_DRAFT);
    setPage(0);
  };

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden">
      <header className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
        <div>
          <h1 className="text-2xl font-bold text-darkPurple">Illustration Logs</h1>
          <p className="text-sm text-gray-600">
            Requests recorded in the <code>IllustrationRequests</code> table, joined with client
            and funding source.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/wizard/illustration-finder"
            className="text-sm font-medium text-primary underline"
          >
            ← Back to finder
          </Link>
          <Button
            variant="outline"
            onClick={() => requestsQuery.refetch()}
            disabled={requestsQuery.isFetching}
          >
            {requestsQuery.isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </header>

      <div className="border-b border-gray-200 px-6 py-4">
        <RequestsFilters
          value={draft}
          onChange={setDraft}
          onApply={handleApply}
          onClear={handleClear}
          isFetching={requestsQuery.isFetching}
        />
      </div>

      <main className="flex-1 overflow-auto px-6 py-4">
        {requestsQuery.isLoading && (
          <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        )}
        {requestsQuery.isError && (
          <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700">
            Failed to load requests.
          </div>
        )}
        {!requestsQuery.isLoading && rows.length === 0 && (
          <div className="p-8 text-center text-sm text-gray-500">No requests match.</div>
        )}

        {rows.length > 0 && (
          <>
            <RequestsTable rows={rows} onRowClick={setSelected} />
            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
              <div>
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <span>
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page + 1 >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </main>

      <RequestDetailDialog row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
